// @ts-nocheck

/* ------------------------------------------------
 *  Intercept pointerleave on <li> items that hold
 *  CTR mega panels so the theme framework doesn't
 *  close the menu while the cursor travels toward
 *  the overlay panel (which lives outside the <li>).
 *  Registered on `window` (capture) so it fires
 *  BEFORE the theme's `document` capture listener.
 * ------------------------------------------------ */
window.addEventListener(
  "pointerleave",
  (event) => {
    const target = event.composedPath?.()?.[0] ?? event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches?.(".menu-list__list-item")) return;

    // Overflow items have panels inside the <li> — no interception needed
    if (target.slot === "overflow") return;

    const source = document.querySelector(
      ".ctr-mega-menu-source[data-ctr-active]"
    );
    if (!source) return;

    const activeTitle = source.getAttribute("data-ctr-active");
    const linkEl = target.querySelector(".menu-list__link-title");
    if (!linkEl) return;

    const linkText = linkEl.textContent.trim().toLowerCase();
    if (linkText !== activeTitle) return;

    const panel = source.querySelector(
      `.ctr-mega-menu-source__panel[data-nav-title="${activeTitle}"]`
    );
    if (!panel) return;

    const panelRect = panel.getBoundingClientRect();
    if (panelRect.height <= 0) return;

    const liRect = target.getBoundingClientRect();
    const cursorBelowLink = event.clientY >= liRect.bottom - 5;
    const cursorAboveEnd = event.clientY <= panelRect.bottom;
    const cursorInX =
      event.clientX >= panelRect.left && event.clientX <= panelRect.right;

    if (cursorBelowLink && cursorAboveEnd && cursorInX) {
      event.stopImmediatePropagation();
    }
  },
  true
);

/* ----------------------------------------
 * Global cleanup for Shopify design-mode
 * ---------------------------------------- */
function ctrMegaCleanupHeader() {
  document.querySelectorAll(".mega-menu[data-ctr-mega-hidden]").forEach((el) => {
    el.style.removeProperty("display");
    el.removeAttribute("data-ctr-mega-hidden");
  });

  document.querySelectorAll("[data-ctr-has-panel]").forEach((el) => {
    el.removeAttribute("data-ctr-has-panel");
  });

  document
    .querySelectorAll(".ctr-mega-menu-source__panel[data-ctr-in-overflow]")
    .forEach((el) => el.remove());

  document
    .querySelectorAll("header-menu > .ctr-mega-menu-source")
    .forEach((el) => el.remove());
}

if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", ctrMegaCleanupHeader);
  document.addEventListener("shopify:section:unload", ctrMegaCleanupHeader);
}

document.addEventListener("alpine:init", () => {
  Alpine.data("ctrMegaScroll", function () {
    return {
      hasOverflow: false,
      canScrollLeft: false,
      canScrollRight: false,

      init() {
        this.$nextTick(() => this.updateArrows());
      },

      updateArrows() {
        const el = this.$refs.track;
        if (!el) return;
        this.hasOverflow = el.scrollWidth > el.clientWidth + 2;
        this.canScrollLeft = el.scrollLeft > 2;
        this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      },

      scrollBy(dir) {
        const el = this.$refs.track;
        if (!el) return;
        const child = el.firstElementChild;
        const step = child
          ? child.offsetWidth + parseInt(getComputedStyle(el).gap || 0)
          : el.clientWidth * 0.8;
        el.scrollBy({ left: dir * step, behavior: "smooth" });
      },
    };
  });

  Alpine.data("ctrMegaMenu", function () {
    return {
      _observer: null,
      _slotObserver: null,
      _panelMap: null,

      init() {
        ctrMegaCleanupHeader();
        this._setup();
        this._observe();
        this._refreshScrollComponents();
      },

      _refreshScrollComponents() {
        this.$nextTick(() => {
          this.$el
            .querySelectorAll("[x-ref='track']")
            .forEach((track) => {
              const scrollEl = track.closest("[x-data='ctrMegaScroll']");
              if (!scrollEl) return;
              const data = Alpine.$data(scrollEl);
              if (data && data.updateArrows) data.updateArrows();
            });
        });
      },

      _setup() {
        const headerMenu = document.querySelector("header-menu");
        if (!headerMenu) return;

        if (this.$el.getAttribute("data-ctr-mounted")) return;
        this.$el.setAttribute("data-ctr-mounted", "");

        headerMenu.appendChild(this.$el);

        const panels = this.$el.querySelectorAll("[data-nav-title]");
        const menuItems = headerMenu.querySelectorAll(".menu-list__list-item");

        const colorClass = [...this.$el.classList].find((c) =>
          c.startsWith("color-")
        );

        this._panelMap = new Map();

        panels.forEach((panel) => {
          const navTitle = panel
            .getAttribute("data-nav-title")
            .trim()
            .toLowerCase();

          panel.setAttribute("data-nav-title", navTitle);

          if (colorClass) panel.classList.add(colorClass);

          for (const item of menuItems) {
            const linkEl = item.querySelector(
              ".menu-list__link .menu-list__link-title"
            );
            if (!linkEl) continue;

            const linkText = linkEl.textContent.trim().toLowerCase();
            if (linkText !== navTitle) continue;

            item.setAttribute("data-ctr-has-panel", navTitle);

            const megaMenu = item.querySelector(
              ".menu-list__submenu-inner > .mega-menu"
            );
            if (megaMenu) {
              megaMenu.style.display = "none";
              megaMenu.setAttribute("data-ctr-mega-hidden", "");
            }

            this._panelMap.set(navTitle, { panel, li: item });
            break;
          }
        });

        this._bindPanelPointerEvents(headerMenu);

        // Move panels into overflow submenu for items already in overflow
        for (const [navTitle, { li }] of this._panelMap) {
          if (li.slot === "overflow") {
            this._moveToOverflow(navTitle);
          }
        }

        // Watch for items moving to/from overflow on resize
        this._slotObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.attributeName !== "slot") continue;
            const li = m.target;
            const navTitle = li.getAttribute("data-ctr-has-panel");
            if (!navTitle) continue;

            if (li.slot === "overflow") {
              this._moveToOverflow(navTitle);
            } else {
              this._moveToSource(navTitle);
            }
          }
        });

        for (const { li } of this._panelMap.values()) {
          this._slotObserver.observe(li, {
            attributes: true,
            attributeFilter: ["slot"],
          });
        }
      },

      _moveToOverflow(navTitle) {
        const entry = this._panelMap.get(navTitle);
        if (!entry) return;

        const { panel, li } = entry;
        const submenuInner = li.querySelector(".menu-list__submenu-inner");
        if (!submenuInner) return;

        panel.removeAttribute("data-ctr-visible");
        panel.setAttribute("data-ctr-in-overflow", "");
        submenuInner.appendChild(panel);
      },

      _moveToSource(navTitle) {
        const entry = this._panelMap.get(navTitle);
        if (!entry) return;

        const { panel } = entry;

        panel.removeAttribute("data-ctr-in-overflow");
        panel.removeAttribute("data-ctr-visible");
        this.$el.appendChild(panel);
      },

      _bindPanelPointerEvents(headerMenu) {
        if (!headerMenu.activate) return;

        let throttle = 0;
        const cancelPendingClose = () => {
          headerMenu.activate(new PointerEvent("pointermove"));
        };

        this.$el
          .querySelectorAll(".ctr-mega-menu-source__panel")
          .forEach((panel) => {
            panel.addEventListener("pointerenter", cancelPendingClose);
            panel.addEventListener(
              "pointermove",
              () => {
                const now = Date.now();
                if (now - throttle < 200) return;
                throttle = now;
                cancelPendingClose();
              },
              { passive: true }
            );
            panel.addEventListener("pointerleave", (e) => {
              // Overflow panels are inside the <li> — theme handles deactivation
              if (panel.hasAttribute("data-ctr-in-overflow")) return;

              const toEl = e.relatedTarget;
              if (
                toEl instanceof Element &&
                toEl.closest(".menu-list__list-item, .ctr-mega-menu-source__panel")
              )
                return;

              const activeLink = headerMenu.querySelector(
                ".menu-list__link[aria-expanded='true']"
              );
              if (!activeLink) return;

              const li = activeLink.closest(".menu-list__list-item");
              if (!li) return;

              li.dispatchEvent(
                new PointerEvent("pointerleave", {
                  bubbles: false,
                  clientX: e.clientX,
                  clientY: e.clientY,
                  relatedTarget: e.relatedTarget,
                })
              );
            });
          });
      },

      _observe() {
        const headerMenu = document.querySelector("header-menu");
        if (!headerMenu) return;

        const source = this.$el;

        const syncPanel = () => {
          const activeLink = headerMenu.querySelector(
            ".menu-list__list-item[data-ctr-has-panel] .menu-list__link[aria-expanded='true']"
          );

          source
            .querySelectorAll("[data-ctr-visible]")
            .forEach((p) => p.removeAttribute("data-ctr-visible"));

          if (!activeLink) {
            source.removeAttribute("data-ctr-active");
            return;
          }

          const li = activeLink.closest(".menu-list__list-item");
          const navTitle = li?.getAttribute("data-ctr-has-panel");
          if (!navTitle) {
            source.removeAttribute("data-ctr-active");
            return;
          }

          source.setAttribute("data-ctr-active", navTitle);

          // Overflow items: panel is inside the submenu — theme handles visibility
          if (li.slot === "overflow") {
            return;
          }

          // Main items: show panel via overlay
          const panel = source.querySelector(
            `.ctr-mega-menu-source__panel[data-nav-title="${navTitle}"]`
          );
          if (panel) {
            panel.setAttribute("data-ctr-visible", "");
            requestAnimationFrame(() => {
              const h = panel.offsetHeight;
              headerMenu.style.setProperty("--submenu-height", `${h}px`);
            });
          }
        };

        this._observer = new MutationObserver(syncPanel);
        this._observer.observe(headerMenu, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-expanded"],
        });
      },

      destroy() {
        if (this._observer) this._observer.disconnect();
        if (this._slotObserver) this._slotObserver.disconnect();

        // Move any overflow panels back to source before cleanup
        if (this._panelMap) {
          for (const [navTitle] of this._panelMap) {
            this._moveToSource(navTitle);
          }
        }

        ctrMegaCleanupHeader();
      },
    };
  });
});
